import React, {useContext} from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

// Utils
import {AuthContext} from "../../contexts/auth.js";
import {SettingsContext} from "../../contexts/settings.js";
import {prettyDateWithDayInUserTimezone, toTitleCase} from "../../utils/helpers.js";
import linkifyText from "./linkifyText.js";

// Components
import Modal from "../../components/Modal.js";
import ModalDelete from "../../components/ModalDelete.js";
import TicketNotesSidebar from "./TicketNotesSidebar.js";

// Style
import {voice} from "../../style/components/typography.js";
import {colors, pad} from "../../style/components/variables.js";
import {flex} from "../../style/components/mixins.js";
import {bp} from "../../style/components/breakpoints.js";
import {
  Pill,
  Heading,
  Text,
  Button,
  Inline,
  Label,
  scrollbar
} from "../../style/components/general.js";

export const STATUS_COLORS = {
  Open: colors.blueLight,
  "In Progress": colors.heroGreen,
  Closed: colors.gray
};

const TicketModal = ({
  ticket,
  visible,
  setVisible,
  typeLabel,
  statusLabel,
  noteDraft,
  setNoteDraft,
  onAddNote,
  onAccept,
  onClose,
  onRequestUpdate,
  onDelete,
  showModalDelete,
  setShowModalDelete,
  deleteLoading,
  requestUpdateAllowed
}) => {
  const {atLeast, roleCanAccessResource} = useContext(AuthContext);
  const {settings} = useContext(SettingsContext);

  if (showModalDelete)
    return (
      <ModalDelete
        visible={showModalDelete}
        setVisible={setShowModalDelete}
        confirmDelete={onDelete}
        title="Delete Ticket"
        prompt={`Are you sure you want to delete ${ticket.key}? This action cannot be undone.`}
        loading={deleteLoading}
      />
    );

  return (
    <Modal
      visible={visible}
      setVisible={setVisible}
      width="920px"
      fixedHeight
      allowScroll={false}>
      <TicketModalBody>
        <TicketModalLayout>
          <TicketMain>
            <Pill color={STATUS_COLORS[statusLabel]}>{statusLabel.toUpperCase()}</Pill>
            <ModalHeading>
              {ticket.key}: {typeLabel}
            </ModalHeading>
            <Heading>{ticket.name}</Heading>
            <Text>
              <strong>Reported By: </strong>
              {ticket.createdBy.firstName} {ticket.createdBy.lastName}
              &nbsp;on&nbsp;
              {prettyDateWithDayInUserTimezone(
                ticket.createdAt,
                settings.timezone,
                "ddd, MMM D YYYY"
              )}
            </Text>
            <br />
            {(Array.isArray(ticket.description)
              ? ticket.description
              : JSON.parse(ticket.description)
            ).map(line => {
              const separator = line.indexOf(":");
              const label = separator === -1 ? line : line.slice(0, separator);
              const value = separator === -1 ? "" : line.slice(separator + 1).trim();
              return (
                <Line key={label}>
                  <Label bold>{toTitleCase(label)}</Label>
                  <Text>{linkifyText(value)}</Text>
                </Line>
              );
            })}
            <br />
            {atLeast("super") && ticket.accepted && ticket.acceptedBy && (
              <Text>
                <strong>Accepted By: </strong>
                {ticket.acceptedBy.firstName} {ticket.acceptedBy.lastName}
                &nbsp;on&nbsp;
                {prettyDateWithDayInUserTimezone(
                  ticket.acceptedAt,
                  settings.timezone,
                  "ddd, MMM D YYYY"
                )}
              </Text>
            )}
            {!ticket.open && (
              <Text>
                <strong>Closed By: </strong>
                {ticket.updatedBy.firstName} {ticket.updatedBy.lastName}
                &nbsp;on&nbsp;
                {prettyDateWithDayInUserTimezone(
                  ticket.updatedAt,
                  settings.timezone,
                  "ddd, MMM D YYYY"
                )}
              </Text>
            )}
          </TicketMain>
          <TicketNotesSidebar
            key={ticket.id}
            ticket={ticket}
            noteDraft={noteDraft}
            setNoteDraft={setNoteDraft}
            onAddNote={onAddNote}
          />
        </TicketModalLayout>
        <TicketOptions>
          {roleCanAccessResource("ticket", "update") && requestUpdateAllowed && ticket.open && (
            <Button type="button" title="Close Ticket" onClick={onRequestUpdate}>
              Request Update
            </Button>
          )}
          {atLeast("super") &&
            roleCanAccessResource("ticket", "update") &&
            ticket.open &&
            !ticket.accepted && (
              <Button type="button" title="Accept Ticket" onClick={onAccept}>
                Accept
              </Button>
            )}
          {atLeast("creator") && roleCanAccessResource("ticket", "update") && ticket.open && (
            <Button type="button" title="Close Ticket" onClick={onClose}>
              Close Ticket
            </Button>
          )}
          {atLeast("super") && roleCanAccessResource("ticket", "delete") && (
            <Delete type="button" title="Delete Ticket" onClick={() => setShowModalDelete(true)}>
              Delete
            </Delete>
          )}
        </TicketOptions>
      </TicketModalBody>
    </Modal>
  );
};

TicketModal.propTypes = {
  ticket: PropTypes.object.isRequired,
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  typeLabel: PropTypes.string.isRequired,
  statusLabel: PropTypes.string.isRequired,
  noteDraft: PropTypes.string.isRequired,
  setNoteDraft: PropTypes.func.isRequired,
  onAddNote: PropTypes.func.isRequired,
  onAccept: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onRequestUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  showModalDelete: PropTypes.bool.isRequired,
  setShowModalDelete: PropTypes.func.isRequired,
  deleteLoading: PropTypes.bool,
  requestUpdateAllowed: PropTypes.bool.isRequired
};

const TicketModalBody = styled.div`
  ${flex("column", "nowrap", "start", "stretch")};
  height: calc(75vh - 100px);
  max-height: calc(75vh - 100px);
  min-height: 240px;
  overflow: hidden;
  margin-bottom: -${pad}px;

  ${bp(1)} {
    height: calc(90vh - 110px);
    max-height: calc(90vh - 110px);
    margin-bottom: -${pad * 1.5}px;
  }

  ${bp(2)} {
    margin-bottom: -${pad * 3}px;
  }
`;

const TicketModalLayout = styled.div`
  ${flex("row", "nowrap", "start", "stretch")};
  position: relative;
  flex: 1;
  min-height: 0;
  gap: 0;
  overflow: hidden;
`;

const TicketMain = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  padding-right: ${pad * 2}px;
  overflow-x: hidden;
  overflow-y: auto;
  ${scrollbar};
`;

const TicketOptions = styled(Inline)`
  flex: 0 0 auto;
  padding-top: ${pad * 2}px;
  padding-bottom: 0;
  margin-bottom: 0;
  gap: ${pad}px;
`;

const ModalHeading = styled.h3`
  ${voice.medium};
  font-weight: bold;
  color: ${props => props.theme.primary};
  margin-top: ${pad}px;
`;

const Line = styled.div`
  padding-bottom: ${pad}px;
`;

const Delete = styled(Button)`
  background: ${props => props.theme.error};
`;

export default TicketModal;
